import { useCallback, useEffect, useRef, useState } from "react";

export type AsyncState<T> =
  | { status: "loading"; data?: undefined; error?: undefined }
  | { status: "ready"; data: T; error?: undefined }
  | { status: "error"; data?: undefined; error: Error };

/**
 * Loads once and exposes the three states every screen in the design draws:
 * skeleton, content, and error. Empty is a property of the loaded data, not a
 * fourth state, so callers check `data.length === 0` themselves.
 *
 * Results arriving after unmount — or after `deps` changed — are dropped. The
 * mocks all sleep before resolving, so a fast tab switch reliably races two
 * loads, and without the guard the slower one wins and paints the wrong screen.
 */
export function useAsync<T>(
  fn: () => Promise<T>,
  deps: unknown[],
): AsyncState<T> & {
  reload: () => void;
} {
  const [state, setState] = useState<AsyncState<T>>({ status: "loading" });
  const [nonce, setNonce] = useState(0);

  // Held in a ref so changing the callback identity every render — which is
  // what an inline arrow does — doesn't retrigger the load.
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    let live = true;
    setState({ status: "loading" });

    fnRef.current().then(
      (data) => {
        if (live) setState({ status: "ready", data });
      },
      (cause: unknown) => {
        if (!live) return;
        const error = cause instanceof Error ? cause : new Error(String(cause));
        setState({ status: "error", error });
      },
    );

    return () => {
      live = false;
    };
    // Deps are spread deliberately: callers pass the reactive inputs, and the
    // fn ref above absorbs the callback identity.
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { ...state, reload };
}
